import { useState, useRef } from 'react';
import { Button, TextButton } from '@safetyhub/ui';
import { api, API_HOST } from '../../lib/api';
import { useAuthStore } from '../../stores/auth';

interface FindingInput {
  description: string;
  category: string;
  subCategory: string;
  severity: string;
  recommendation: string;
  measures: string[];
  location: string;
}

interface GenerationResult {
  imageUrl: string;
  mimeType: string;
  text?: string;
  prompt?: string;
  summary?: string;
  model?: string;
}

interface AnalysisResult {
  findings: FindingInput[];
}

/** 모델 정의 */
const IMAGE_MODELS = [
  { id: 'gemini-2.5-flash-image', label: '2.5 Flash Image', color: 'blue' },
  { id: 'gemini-3.1-flash-image-preview', label: '3.1 Flash Image', color: 'teal' },
  { id: 'gemini-3-pro-image-preview', label: '3 Pro Image', color: 'amber' },
] as const;

type ModelId = (typeof IMAGE_MODELS)[number]['id'];

/** 모델별 A/B 결과 */
interface ModelResults {
  direct: GenerationResult | null;
  directError: string;
  directElapsed: number;
  summarized: GenerationResult | null;
  summarizedError: string;
  summarizedElapsed: number;
}

const EMPTY_MODEL_RESULTS: ModelResults = {
  direct: null,
  directError: '',
  directElapsed: 0,
  summarized: null,
  summarizedError: '',
  summarizedElapsed: 0,
};

const EMPTY_FINDING: FindingInput = {
  description: '',
  category: '',
  subCategory: '',
  severity: '',
  recommendation: '',
  measures: [],
  location: '',
};

const SEVERITY_COLORS: Record<string, string> = {
  CRITICAL: 'bg-red-100 text-red-800 border-red-200',
  HIGH: 'bg-orange-100 text-orange-800 border-orange-200',
  MEDIUM: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  LOW: 'bg-green-100 text-green-800 border-green-200',
};

const SEVERITY_LABELS: Record<string, string> = {
  CRITICAL: '심각',
  HIGH: '높음',
  MEDIUM: '보통',
  LOW: '낮음',
};

/** 모델별 컬러 팔레트 */
const MODEL_STYLES: Record<string, { bg: string; border: string; text: string; badge: string }> = {
  blue: { bg: 'bg-blue-50', border: 'border-blue-100', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-800' },
  teal: { bg: 'bg-teal-50', border: 'border-teal-100', text: 'text-teal-700', badge: 'bg-teal-100 text-teal-800' },
  amber: { bg: 'bg-amber-50', border: 'border-amber-100', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-800' },
};

export default function AiImageTestPage() {
  // 조직 식별자 — currentMembership.companyId. /files/upload엔 `companyId`, /ai/generate-improvement-image*엔
  // `organizationId`라는 이름으로 보낸다(같은 값, 엔드포인트별 계약 이름이 다르다).
  const currentOrganizationId = useAuthStore((state) => state.currentMembership?.companyId ?? null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [uploadedPhotoUrl, setUploadedPhotoUrl] = useState<string>('');
  const [findings, setFindings] = useState<FindingInput[]>([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 모델별 결과 상태
  const [results, setResults] = useState<Record<ModelId, ModelResults>>(() => {
    const init: Record<string, ModelResults> = {};
    IMAGE_MODELS.forEach((m) => { init[m.id] = { ...EMPTY_MODEL_RESULTS }; });
    return init as Record<ModelId, ModelResults>;
  });

  const updateResult = (modelId: ModelId, patch: Partial<ModelResults>) => {
    setResults((prev) => ({ ...prev, [modelId]: { ...prev[modelId], ...patch } }));
  };

  // 사진 선택 → 업로드 → AI 분석
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 초기화
    const resetResults: Record<string, ModelResults> = {};
    IMAGE_MODELS.forEach((m) => { resetResults[m.id] = { ...EMPTY_MODEL_RESULTS }; });
    setResults(resetResults as Record<ModelId, ModelResults>);
    setError('');
    setFindings([]);

    const reader = new FileReader();
    reader.onload = () => setPreviewUrl(reader.result as string);
    reader.readAsDataURL(file);

    setAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('companyId', currentOrganizationId || '');
      formData.append('purpose', 'INSPECTION_PHOTO');

      const uploadRes = await api.post<{
        success: boolean;
        data: { url: string };
      }>('/files/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const photoUrl = uploadRes.data.data.url.startsWith('http')
        ? uploadRes.data.data.url
        : `${API_HOST}${uploadRes.data.data.url}`;
      setUploadedPhotoUrl(photoUrl);

      const analysisRes = await api.post<{
        success: boolean;
        data: AnalysisResult;
      }>('/ai/analyze-photo', { photoUrl, industryType: '제조업' });

      if (analysisRes.data.success && analysisRes.data.data.findings.length > 0) {
        setFindings(analysisRes.data.data.findings);
      } else {
        setFindings([{ ...EMPTY_FINDING }]);
      }
    } catch (err: unknown) {
      setError(`사진 분석 실패: ${extractError(err)}`);
      setFindings([{ ...EMPTY_FINDING }]);
    } finally {
      setAnalyzing(false);
    }
  };

  const updateFinding = (index: number, field: keyof FindingInput, value: string | string[]) => {
    setFindings((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addFinding = () => setFindings((prev) => [...prev, { ...EMPTY_FINDING }]);

  const removeFinding = (index: number) => {
    if (findings.length <= 1) return;
    setFindings((prev) => prev.filter((_, i) => i !== index));
  };

  const hasValidFinding = findings.some((f) => f.description.trim());

  // 3모델 x 2방식 = 6개 병렬 호출
  const handleGenerate = async () => {
    if (!uploadedPhotoUrl || !hasValidFinding) return;

    setGenerating(true);
    const resetResults: Record<string, ModelResults> = {};
    IMAGE_MODELS.forEach((m) => { resetResults[m.id] = { ...EMPTY_MODEL_RESULTS }; });
    setResults(resetResults as Record<ModelId, ModelResults>);

    // 서버 계약(`ai-image.controller.ts` GenerateImprovementImageDto / FindingInput)에 맞춘 본문 — 정본은 실사용
    // 경로 `lib/composerImprovementImage.ts`와 같다. 이 화면의 FindingInput은 분석 응답(`severity` 포함)을 그대로
    // 들고 있어 그대로 보내면 서버가 모르는 `severity`가 섞인다(whitelist DTO면 400). 화면 표시용 필드는 여기서 걷는다.
    const validFindings = findings
      .filter((f) => f.description.trim())
      .map(({ description, category, subCategory, recommendation, measures, location }) => ({
        description,
        category,
        subCategory,
        recommendation,
        measures: measures.filter((m) => m.trim()),
        location,
      }));

    const promises: Promise<void>[] = [];

    for (const model of IMAGE_MODELS) {
      // 조직 키 이름은 `organizationId`다(값은 currentMembership.companyId) — `companyId`로 보내면 서버에서 undefined가
      // 되어 저장 경로가 `undefined/improvement-images/…`로 떨어졌다(2026-09-19 리뷰).
      const payload = {
        photoUrl: uploadedPhotoUrl,
        findings: validFindings,
        organizationId: currentOrganizationId || '',
        model: model.id,
      };

      // A: 직접 생성
      promises.push((async () => {
        const start = Date.now();
        try {
          const res = await api.post<{
            success: boolean;
            data: GenerationResult;
            error: { message: string } | null;
          }>('/ai/generate-improvement-image', payload);
          const elapsed = Date.now() - start;
          if (res.data.success) {
            updateResult(model.id, { direct: res.data.data, directElapsed: elapsed });
          } else {
            updateResult(model.id, { directError: res.data.error?.message || '알 수 없는 오류', directElapsed: elapsed });
          }
        } catch (err) {
          updateResult(model.id, { directError: extractError(err), directElapsed: Date.now() - start });
        }
      })());

      // B: 종합 → 생성
      promises.push((async () => {
        const start = Date.now();
        try {
          const res = await api.post<{
            success: boolean;
            data: GenerationResult;
            error: { message: string } | null;
          }>('/ai/generate-improvement-image-summarized', payload);
          const elapsed = Date.now() - start;
          if (res.data.success) {
            updateResult(model.id, { summarized: res.data.data, summarizedElapsed: elapsed });
          } else {
            updateResult(model.id, { summarizedError: res.data.error?.message || '알 수 없는 오류', summarizedElapsed: elapsed });
          }
        } catch (err) {
          updateResult(model.id, { summarizedError: extractError(err), summarizedElapsed: Date.now() - start });
        }
      })());
    }

    await Promise.allSettled(promises);
    setGenerating(false);
  };

  const hasAnyResult = IMAGE_MODELS.some((m) => {
    const r = results[m.id];
    return r.direct || r.summarized || r.directError || r.summarizedError;
  });

  const isLoading = analyzing || generating;

  return (
    <div className="min-h-screen bg-ink-50 p-6">
      <div className="max-w-[1600px] mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-ink-900">
            AI 개선 이미지 생성 PoC
          </h1>
          <p className="mt-1 text-sm text-ink-600">
            사진 업로드 → AI 위험요인 분석 → 3개 모델 x 2가지 방식 비교
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {IMAGE_MODELS.map((m) => (
              <span key={m.id} className={`text-xs px-2 py-1 rounded ${MODEL_STYLES[m.color].badge}`}>
                {m.label} ({m.id})
              </span>
            ))}
          </div>
        </div>

        {/* 입력 영역 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
          <div className="bg-white rounded-card border p-5">
            <h2 className="font-semibold text-ink-900 mb-3">1. 점검 사진</h2>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileSelect}
              className="hidden"
            />
            {previewUrl ? (
              <div className="space-y-2">
                <div className="relative">
                  <img
                    src={previewUrl}
                    alt="원본"
                    className="w-full rounded-card-sm border max-h-56 object-contain bg-ink-100"
                  />
                  {analyzing && (
                    <div className="absolute inset-0 bg-black/40 rounded-card-sm flex items-center justify-center">
                      <div className="text-center text-white">
                        <Spinner />
                        <p className="text-sm font-medium mt-2">위험요인 분석 중...</p>
                      </div>
                    </div>
                  )}
                </div>
                <TextButton
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isLoading}
                  className="text-sm"
                >
                  다른 사진 선택
                </TextButton>
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full h-36 border-2 border-dashed border-ink-300 rounded-card flex flex-col items-center justify-center text-ink-500 hover:border-brand-400 hover:text-brand-600 transition-colors"
              >
                <svg className="w-8 h-8 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-sm">클릭하여 사진 선택 (자동 분석)</span>
              </button>
            )}
          </div>

          {findings.length > 0 && (
            <div className="lg:col-span-2 bg-white rounded-card border p-5 max-h-[500px] overflow-y-auto">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold text-ink-900">
                  2. 발견사항
                  <span className="ml-2 text-sm font-normal text-ink-500">
                    ({findings.filter((f) => f.description.trim()).length}건 — 수정 가능)
                  </span>
                </h2>
                <Button size="xs" onClick={addFinding}>
                  + 수동 추가
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {findings.map((f, idx) => (
                  <FindingCard
                    key={idx}
                    finding={f}
                    index={idx}
                    total={findings.length}
                    onUpdate={updateFinding}
                    onRemove={removeFinding}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 생성 버튼 */}
        {findings.length > 0 && (
          <div className="mb-6">
            <Button
              variant="primary"
              size="lg"
              onClick={handleGenerate}
              disabled={!uploadedPhotoUrl || !hasValidFinding || isLoading}
              className="w-full"
            >
              {generating ? (
                <span className="flex items-center justify-center gap-2">
                  <Spinner size="sm" />
                  3개 모델 x 2가지 방식 생성 중... (최대 5분)
                </span>
              ) : (
                '개선 이미지 생성 (3 모델 x A/B 비교)'
              )}
            </Button>
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-card p-4 text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* 결과: 모델별 행 */}
        {(hasAnyResult || generating) && (
          <div className="space-y-6">
            {IMAGE_MODELS.map((model) => {
              const r = results[model.id];
              const style = MODEL_STYLES[model.color];
              const hasResult = r.direct || r.summarized || r.directError || r.summarizedError;
              const isModelLoading = generating && !hasResult;

              return (
                <div key={model.id} className="bg-white rounded-card border p-5">
                  {/* 모델 헤더 */}
                  <div className="flex items-center gap-3 mb-4">
                    <span className={`text-sm font-semibold px-2.5 py-1 rounded ${style.badge}`}>
                      {model.label}
                    </span>
                    <span className="text-xs text-ink-400">{model.id}</span>
                    {isModelLoading && (
                      <span className="flex items-center gap-1 text-xs text-ink-400">
                        <Spinner size="sm" /> 생성 중...
                      </span>
                    )}
                  </div>

                  {/* 3열: 원본 | A. 직접 | B. 종합→생성 */}
                  <div className="grid grid-cols-3 gap-4">
                    {/* 원본 */}
                    <div>
                      <div className="text-center mb-2">
                        <span className="text-sm font-medium text-ink-700">원본</span>
                      </div>
                      <img
                        src={previewUrl}
                        alt="원본"
                        className="w-full rounded-card-sm border object-contain bg-ink-100"
                        style={{ maxHeight: '360px' }}
                      />
                    </div>

                    {/* A: 직접 생성 */}
                    <div>
                      <div className="text-center mb-2">
                        <span className={`text-sm font-medium ${style.text}`}>A. 직접 생성</span>
                        {r.directElapsed > 0 && (
                          <span className="ml-2 text-xs text-ink-400">{(r.directElapsed / 1000).toFixed(1)}s</span>
                        )}
                      </div>
                      <ImageResultBox
                        result={r.direct}
                        error={r.directError}
                        loading={generating && !r.direct && !r.directError}
                      />
                    </div>

                    {/* B: 종합→생성 */}
                    <div>
                      <div className="text-center mb-2">
                        <span className={`text-sm font-medium ${style.text}`}>B. 종합 → 생성</span>
                        {r.summarizedElapsed > 0 && (
                          <span className="ml-2 text-xs text-ink-400">{(r.summarizedElapsed / 1000).toFixed(1)}s</span>
                        )}
                      </div>
                      <ImageResultBox
                        result={r.summarized}
                        error={r.summarizedError}
                        loading={generating && !r.summarized && !r.summarizedError}
                      />
                    </div>
                  </div>

                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── 하위 컴포넌트 ──

function Spinner({ size = 'md' }: { size?: 'sm' | 'md' }) {
  const cls = size === 'sm' ? 'h-4 w-4' : 'h-8 w-8';
  return (
    <svg className={`animate-spin ${cls}`} viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

function ImageResultBox({
  result,
  error,
  loading,
}: {
  result: GenerationResult | null;
  error: string;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="w-full rounded-card-sm border bg-ink-50 flex items-center justify-center" style={{ minHeight: '260px' }}>
        <div className="text-center text-ink-400">
          <Spinner />
          <p className="text-xs mt-2">생성 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full rounded-card-sm border border-red-200 bg-red-50 flex items-center justify-center p-4" style={{ minHeight: '260px' }}>
        <p className="text-xs text-red-600 text-center">{error}</p>
      </div>
    );
  }

  if (result) {
    return (
      <img
        src={result.imageUrl}
        alt="개선 이미지"
        className="w-full rounded-card-sm border object-contain bg-ink-100"
        style={{ maxHeight: '360px' }}
      />
    );
  }

  return null;
}

function FindingCard({
  finding: f,
  index: idx,
  total,
  onUpdate,
  onRemove,
}: {
  finding: FindingInput;
  index: number;
  total: number;
  onUpdate: (i: number, field: keyof FindingInput, value: string | string[]) => void;
  onRemove: (i: number) => void;
}) {
  return (
    <div className="border rounded-card p-3 bg-ink-50 space-y-2 text-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-medium text-ink-700">#{idx + 1}</span>
          {f.severity && (
            <span className={`text-xs px-1.5 py-0.5 rounded-full border ${SEVERITY_COLORS[f.severity] || 'bg-ink-100 text-ink-700'}`}>
              {SEVERITY_LABELS[f.severity] || f.severity}
            </span>
          )}
        </div>
        {total > 1 && (
          <TextButton tone="danger" onClick={() => onRemove(idx)} className="text-xs">삭제</TextButton>
        )}
      </div>

      <div className="grid grid-cols-2 gap-1.5">
        <input type="text" value={f.category} onChange={(e) => onUpdate(idx, 'category', e.target.value)}
          placeholder="대분류" className="border rounded-control px-2 py-1 text-xs" />
        <input type="text" value={f.subCategory} onChange={(e) => onUpdate(idx, 'subCategory', e.target.value)}
          placeholder="세부분류" className="border rounded-control px-2 py-1 text-xs" />
      </div>

      <textarea value={f.description} onChange={(e) => onUpdate(idx, 'description', e.target.value)}
        placeholder="위험 상황 설명 *" rows={2} className="w-full border rounded-control px-2 py-1 text-xs resize-none" />

      <input type="text" value={f.location} onChange={(e) => onUpdate(idx, 'location', e.target.value)}
        placeholder="위치" className="w-full border rounded-control px-2 py-1 text-xs" />

      <div>
        <div className="flex items-center justify-between mb-0.5">
          <span className="text-xs text-ink-500">개선 조치</span>
          <TextButton onClick={() => onUpdate(idx, 'measures', [...f.measures, ''])}
            className="text-xs">+ 추가</TextButton>
        </div>
        {f.measures.length > 0 ? (
          <div className="space-y-1">
            {f.measures.map((m, mIdx) => (
              <div key={mIdx} className="flex gap-1">
                <input type="text" value={m}
                  onChange={(e) => {
                    const next = [...f.measures]; next[mIdx] = e.target.value;
                    onUpdate(idx, 'measures', next);
                  }}
                  placeholder={`조치 ${mIdx + 1}`} className="flex-1 border rounded-control px-2 py-1 text-xs" />
                <TextButton tone="danger" onClick={() => onUpdate(idx, 'measures', f.measures.filter((_, i) => i !== mIdx))}
                  aria-label="개선대책 삭제" className="text-xs px-1">x</TextButton>
              </div>
            ))}
          </div>
        ) : (
          <input type="text" value={f.recommendation} onChange={(e) => onUpdate(idx, 'recommendation', e.target.value)}
            placeholder="개선 권고" className="w-full border rounded-control px-2 py-1 text-xs" />
        )}
      </div>
    </div>
  );
}

function extractError(err: unknown): string {
  return (
    (err as { response?: { data?: { error?: { message?: string } } } })
      ?.response?.data?.error?.message ||
    (err as Error).message ||
    '요청 실패'
  );
}
